import React, { forwardRef, useMemo, useState, useEffect } from 'react';
import classNames from 'classnames';

import { Handle } from './handle';
import { Remove } from './remove';
import debounce from 'lodash/debounce';
import { isCmdZ } from '../../utils/keyboard';
import { ContentEditableInput } from '../content-editable-input';
import { useRefCallback } from 'src/utils/react/ref-callback.hook';
import { useEditorStore } from '../../editor-store';

export interface FolderProps {
  children: React.ReactNode;
  columns?: number;
  label: string;
  style?: React.CSSProperties;
  horizontal?: boolean;
  hover?: boolean;
  isActivelyOverContainer?: boolean;
  handleProps?: React.HTMLAttributes<any>;
  scrollable?: boolean;
  shadow?: boolean;
  placeholder?: boolean;
  unstyled?: boolean;
  onClick?(): void;
  onRemove?(): void;
  numberOfItems?: number;
  onFolderNameChange: (name: string) => void;
  isDraggin?: boolean;
  isAlone?: boolean;
  isMoving?: boolean;
}

// eslint-disable-next-line react/display-name
export const Folder = forwardRef<HTMLDivElement, FolderProps>(
  (
    {
      children,
      columns = 1,
      handleProps,
      horizontal,
      hover,
      isActivelyOverContainer = false,
      onClick,
      onRemove,
      label,
      placeholder,
      style,
      scrollable,
      shadow,
      unstyled,
      numberOfItems = 0,
      onFolderNameChange,
      isAlone = false,
      isDraggin = false,
      isMoving = false,
      ...props
    }: FolderProps,
    ref
  ) => {
    const [localFolderName, setLocalFolderName] = useState(label);
    const isFolder = numberOfItems && numberOfItems > 1;
    const hasOneItemInside = numberOfItems && numberOfItems === 1;
    const empty = numberOfItems === 0 || numberOfItems === undefined;

    const { sectionDrawerOpen } = useEditorStore();

    useEffect(() => {
      setLocalFolderName(label);
    }, [label]);

    const onChange = useRefCallback(
      (name: string) => {
        if (name !== label) {
          onFolderNameChange(name);
        }
      },
      [onFolderNameChange, label]
    );

    const debounceOnChange = useMemo(() => debounce(onChange, 500), [onChange]);

    const handlePaste = (event: React.ClipboardEvent) => {
      event.preventDefault();
      const paste = event?.clipboardData?.getData('text') || '';
      const selection = window.getSelection();
      selection?.deleteFromDocument();
      selection?.getRangeAt(0).insertNode(document.createTextNode(paste));
      const updatedFolderName = (event.target as HTMLDivElement).innerText || '';
      onChange(updatedFolderName);
    };

    return (
      <div
        ref={ref}
        className={classNames('flex flex-row group/folder relative w-[280px]', {
          'z-50 scale-[1] bg-transparent': isDraggin,
        })}
        onClick={onClick}
        style={{ ...style }}
        tabIndex={onClick ? 0 : undefined}
        {...props}
      >
        <div
          className={classNames('flex flex-1 flex-col auto-rows-max overflow-hidden rounded-2xl my-0 transition-all', {
            'py-0 bg-gray-100-50': hasOneItemInside,
            'bg-neue-canvas-fg-5 hover:bg-neue-canvas-fg-10 text-black my-2 pb-3': isFolder || isActivelyOverContainer,
            'opacity-75': hover && !isDraggin,
            'bg-neue-canvas-fg-5 border-dashed h-12 my-2': empty,
            'mr-16': !sectionDrawerOpen,
            'border-px border-neue-canvas-fg': isActivelyOverContainer,
          })}
        >
          {isFolder || isActivelyOverContainer ? (
            <div
              className={classNames('w-full items-center justify-between', {
                'py-3 pl-6 pr-4 pb-2 h-full': isFolder || isActivelyOverContainer,
                'hidden h-0': hasOneItemInside && !isActivelyOverContainer,
                'flex h-auto': !hasOneItemInside || isActivelyOverContainer,
              })}
            >
              <div className='flex flex-1 text-neue-journey-small'>
                <ContentEditableInput
                  placeholder='Untitled'
                  value={localFolderName}
                  className='text-nowrap truncate w-full max-w-[220px] text-neue-canvas-fg'
                  onChange={(value) => {
                    setLocalFolderName(value);
                    debounceOnChange(value);
                  }}
                  onPaste={(event: React.ClipboardEvent) => handlePaste(event)}
                  maxLength={50}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.currentTarget.blur();
                    } else if (isCmdZ(e.nativeEvent)) {
                      debounceOnChange.cancel();
                      const v = (e.target as HTMLDivElement).innerText || '';
                      onChange(v);
                    }
                  }}
                  onKeyPress={(e) => {
                    if ((e.target as HTMLDivElement).innerText.length > 50) {
                      e.preventDefault();
                    }
                  }}
                />
              </div>

              {onRemove ? <Remove onClick={onRemove} /> : undefined}
            </div>
          ) : null}

          {children && <ul>{children}</ul>}
        </div>
        {!isAlone && (
          <div
            className={classNames(
              'flex opacity-0 transition-opacity group-hover/folder:opacity-100 justify-center items-center flex-row ml-auto pl-2'
            )}
          >
            <Handle {...handleProps} />
          </div>
        )}
      </div>
    );
  }
);
